<template>
  <div class="flex w-auto items-center">
    <SmDropdown
      class="workspace"
      type="panel"
      :close-on-click-inside="false"
      dropdown-class="min-w-[15rem] w-[20rem] max-w-7xl">
      <template #trigger>
        <div
          class="flex h-[32px] w-full cursor-pointer items-center gap-3 px-4">
          <div>
            <TextMultiline :text="selectedSubAccountLabel" class="max-w-xs" />
          </div>
          <ChevronsUpDown :size="18" class="ml-auto flex-shrink-0" />
        </div>
      </template>
      <div class="flex flex-col gap-1 p-2">
        <SmInput
          v-model="searchQuery"
          class="mb-1 w-full"
          size="small"
          outline
          :label="i18n.t('searchWorkspace')" />
        <div class="flex max-h-64 flex-col gap-1 overflow-y-auto">
          <SmDropdownItem
            v-for="option in filteredSubAccountOptions"
            :key="option.value"
            :selected="option.value === activeSubAccountId"
            @click="handleSelect(option)">
            <span :class="{ 'font-bold': option.value === activeSubAccountId }">
              {{ option.label }}
            </span>
            <div class="ml-auto">
              <Check
                v-if="option.value === activeSubAccountId"
                class="text-contrast"
                :size="18" />
            </div>
          </SmDropdownItem>
          <div
            v-if="filteredSubAccountOptions.length === 0"
            class="p-2 text-sm text-muted">
            {{ i18n.t('noResults') }}
          </div>
        </div>
      </div>
    </SmDropdown>
  </div>
</template>

<script lang="ts" setup>
  import { SoftwareOut } from '@/client'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { ChevronsUpDown, Check } from 'lucide-vue-next'
  import { Option } from '@/components/sm/SmSelect.vue'

  const props = defineProps({
    application: {
      type: Object as PropType<SoftwareOut>,
      required: true,
    },
  })

  const route = useRoute()
  const i18n = useI18n()

  const activeSubAccountId = defineModel<string>('activeSubAccountId', {
    default: '',
  })

  const searchQuery = ref('')

  const subAccountsSSOFilters = computed(() => {
    if (route.query.sso === 'true' && activeSubAccountId.value) {
      return [props.application.sub_accounts[activeSubAccountId.value]]
    }

    return subAccountsSorted.value
  })

  const subAccountOptions = computed<Option<string>[]>(() =>
    subAccountsSSOFilters.value.map((sa) => ({
      label: sa.name,
      value: sa.id,
    }))
  )

  // Sort sub accounts by sorting (integer)
  const subAccountsSorted = computed(() => {
    const subAccounts = props.application?.sub_accounts
    if (subAccounts === undefined) {
      return []
    }

    return Object.values(subAccounts).sort((a, b) => {
      // If sorting is undefined, sort it to the end
      if (a.sorting === undefined || b.sorting === undefined) {
        return 0
      }
      return a.sorting - b.sorting
    })
  })

  const filteredSubAccountOptions = computed(() => {
    const query = searchQuery.value.trim().toLowerCase()

    if (!query) return subAccountOptions.value
    return subAccountOptions.value.filter((option) =>
      option.label.toLowerCase().includes(query)
    )
  })

  watch(
    subAccountOptions,
    (options) => {
      if (!activeSubAccountId.value && options.length > 0) {
        activeSubAccountId.value = options[0].value
      }
    },
    { immediate: true }
  )

  const selectedSubAccountLabel = computed(() => {
    const subaccount = subAccountOptions.value.find(
      (sa) => sa.value === activeSubAccountId.value
    )
    return subaccount ? subaccount.label : i18n.t('selectSubaccount')
  })

  function handleSelect(option: Option<string>) {
    activeSubAccountId.value = option.value
  }
</script>

<style lang="css" scoped>
  .workspace {
    border: 1px solid #121212;
    border-radius: 20px;
  }
</style>
